import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardMedia,
  TextField,
  InputAdornment,
  Button,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  IconButton,
  ToggleButtonGroup,
  ToggleButton,
  Menu,
  MenuItem,
  Switch,
  FormControlLabel,
  Snackbar,
  Alert,
  type SxProps,
  type Theme,
} from '@mui/material';
import {
  Restaurant as RestaurantIcon,
  Add,
  Search as SearchIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ViewList as ViewListIcon,
  ViewModule as ViewModuleIcon,
  MenuBook as MenuBookIcon,
} from '@mui/icons-material';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { useSubscription } from '../contexts/SubscriptionContext';
import { useLimits } from '../hooks/useLimits';
import {
  getUserRecipes,
  createRecipe,
  updateRecipe,
  deleteRecipe,
  setWholeBookPublic,
  isBookPublic,
  Recipe
} from '../services/recipesService';
import { UpgradePrompt } from '../components/subscription/UpgradePrompt';
import { LimitReachedDialog } from '../components/subscription/LimitReachedDialog';
import { RecipesDialog } from '../components/recipes/RecipesDialog';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { BottomNavigation } from '../layout/BottomNavigation';
import { TopBar } from '../layout/TopBar';

export const RecipesPage: React.FC = () => {
  const { theme } = useTheme();
  const { user, hostlyUser, loading: authLoading } = useAuth();
  const { plan } = useSubscription();
  const {
    canAddRecipe,
    recipesRemaining,
    isLoading: limitsLoading,
    checkRecipeLimit,
    refreshLimits
  } = useLimits();

  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [recipes, setRecipes] = useState<Recipe[]>([]);
  const [recipeLoading, setRecipeLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [limitDialog, setLimitDialog] = useState({ open: false, message: '' });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedRecipe, setSelectedRecipe] = useState<Recipe | null>(null);
  const [dialogMode, setDialogMode] = useState<'create' | 'edit'>('create');

  useEffect(() => {
    if (searchParams.get('new') === '1') {
      setSelectedRecipe(null);
      setDialogMode('create');
      setDialogOpen(true);
      searchParams.delete('new');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [recipeToDelete, setRecipeToDelete] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [viewMode, setViewMode] = useState<'list' | 'grid'>('list');
  const [moreMenuAnchor, setMoreMenuAnchor] = useState<null | HTMLElement>(null);
  const [bookPublic, setBookPublic] = useState(false);
  const [bookBusy, setBookBusy] = useState(false);

  useEffect(() => {
    const loadBookFlag = async () => {
      if (!user?.uid) return;
      try {
        setBookPublic(await isBookPublic(user.uid));
      } catch {
        setBookPublic(false);
      }
    };
    loadBookFlag();
  }, [user?.uid]);

  const handleToggleBookPublic = async (makePublic: boolean) => {
    if (!user?.uid) return;
    setBookBusy(true);
    try {
      const authorName = `${hostlyUser?.name || ''} ${hostlyUser?.lastName || ''}`.trim() || 'Anônimo';
      const total = await setWholeBookPublic(user.uid, makePublic, authorName);
      setBookPublic(makePublic);
      const refreshed = await getUserRecipes(user.uid);
      setRecipes(refreshed);
      setLimitDialog({ open: false, message: '' });
      setBookMessage(makePublic
        ? `Livro publicado: ${total} receita(s) agora aparecem para todos.`
        : `Livro despublicado: ${total} receita(s) voltaram a ser privadas.`);
    } catch {
      setBookError('Erro ao atualizar o livro. Tente de novo.');
    } finally {
      setBookBusy(false);
    }
  };

  const [bookMessage, setBookMessage] = useState<string | null>(null);
  const [bookError, setBookError] = useState<string | null>(null);

  // Categorias de receitas
  const categories = [
    'Bolos',
    'Massas',
    'Risotos',
    'Carnes',
    'Peixes',
    'Aves',
    'Saladas',
    'Sopas',
    'Molhos',
    'Doces',
    'Bebidas',
    'Petiscos',
    'Pratos Vegetarianos',
    'Pratos Veganos',
    'Sem Glúten',
    'Outro',
  ];

  // Carregar receitas do usuário
  useEffect(() => {
    const loadRecipes = async () => {
      if (!user?.uid) {
        setRecipeLoading(false);
        return;
      }

      try {
        setRecipeLoading(true);
        const userRecipes = await getUserRecipes(user.uid);
        setRecipes(userRecipes);
      } catch (error) {
        console.error('Erro ao carregar receitas:', error);
        setRecipes([]);
      } finally {
        setRecipeLoading(false);
      }
    };

    loadRecipes();
  }, [user?.uid]);

  // Filtrar receitas pela busca e categoria
  const displayRecipes = recipes.filter(recipe => {
    const matchesSearch = !searchTerm.trim() ||
      recipe.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      recipe.ingredients.some(ing => ing.name.toLowerCase().includes(searchTerm.toLowerCase()));

    const matchesCategory = !selectedCategory || recipe.category === selectedCategory;

    return matchesSearch && matchesCategory;
  });

  const handleAddRecipe = async () => {
    const limitCheck = await checkRecipeLimit();

    if (!limitCheck.allowed) {
      setLimitDialog({
        open: true,
        message: limitCheck.message || 'Limite de receitas atingido'
      });
      return;
    }

    setSelectedRecipe(null);
    setDialogMode('create');
    setDialogOpen(true);
  };

  const handleEditClick = (e: React.MouseEvent, recipe: Recipe) => {
    e.stopPropagation();
    setSelectedRecipe(recipe);
    setDialogMode('edit');
    setDialogOpen(true);
  };

  const handleViewClick = (recipe: Recipe) => {
    navigate(`/recipes/${recipe.id}`);
  };

  const handleSaveRecipe = async (recipeData: Omit<Recipe, 'id'>) => {
    if (!user?.uid) return;

    try {
      const authorName = `${hostlyUser?.name || ''} ${hostlyUser?.lastName || ''}`.trim() || 'Anônimo';
      await createRecipe(user.uid, {
        ...recipeData,
        ...(bookPublic ? { isPublic: true, authorName } : {}),
      });
      await refreshLimits();
      const userRecipes = await getUserRecipes(user.uid);
      setRecipes(userRecipes);
    } catch (error) {
      console.error('Erro ao criar receita:', error);
      throw error;
    }
  };

  const handleUpdateRecipe = async (recipeId: string, updates: Partial<Recipe>) => {
    if (!user?.uid) return;

    try {
      await updateRecipe(user.uid, recipeId, updates);
      const userRecipes = await getUserRecipes(user.uid);
      setRecipes(userRecipes);
    } catch (error) {
      console.error('Erro ao atualizar receita:', error);
      throw error;
    }
  };

  const handleDeleteRecipe = async (recipeId: string) => {
    if (!user?.uid) return;

    try {
      await deleteRecipe(user.uid, recipeId);
      await refreshLimits();
      const userRecipes = await getUserRecipes(user.uid);
      setRecipes(userRecipes);
    } catch (error) {
      console.error('Erro ao deletar receita:', error);
      throw error;
    }
  };

  const handleUpgrade = () => {
    navigate('/pricing');
  };

  const handleDeleteClick = (e: React.MouseEvent, recipeId: string) => {
    e.stopPropagation();
    setRecipeToDelete(recipeId);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!recipeToDelete || !user?.uid) return;

    setDeleting(true);
    try {
      await handleDeleteRecipe(recipeToDelete);
      setDeleteDialogOpen(false);
      setRecipeToDelete(null);
    } catch (error) {
      alert('Erro ao excluir receita');
    } finally {
      setDeleting(false);
    }
  };

  // Loading state
  if (authLoading || limitsLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress sx={{ color: theme.colors.primary }} />
      </Box>
    );
  }

  // Require authentication
  if (!user) {
    navigate('/login');
    return null;
  }

  // Estilos tipados
  const containerStyles: SxProps<Theme> = {
    flex: 1,
    pb: 10,
    pt: '70px',
    px: { xs: 2, sm: 3 },
    maxWidth: '1200px',
    margin: '0 auto',
    width: '100%',
  };

  const searchFieldStyles: SxProps<Theme> = {
    mb: { xs: 2, sm: 3 },
    '& .MuiOutlinedInput-root': {
      borderRadius: theme.radii.medium,
      backgroundColor: theme.colors.card,
      fontSize: { xs: '0.95rem', sm: '1rem' }
    }
  };

  const recipeCardStyles: SxProps<Theme> = {
    display: 'flex',
    height: { xs: 70, sm: 80 },
    backgroundColor: theme.colors.card,
    borderRadius: theme.radii.medium,
    border: `1px solid ${theme.colors.border}`,
    cursor: 'pointer',
    transition: 'all 0.2s',
    overflow: 'hidden',
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: theme.shadows.medium,
      borderColor: theme.colors.primary,
    }
  };



  return (
    <Box
      sx={{
        minHeight: '100vh',
        backgroundColor: theme.colors.background,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <TopBar
        title="Receitas"
        showBackButton
        showProfileButton
        showSearchButton
        showMoreButton
        onBackClick={() => navigate('/dashboard')}
        onSearchClick={() => {
          const searchInput = document.querySelector('input[placeholder*="Buscar"]') as HTMLInputElement;
          if (searchInput) {
            searchInput.focus();
            searchInput.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }}
        onMoreClick={(e) => setMoreMenuAnchor(e?.currentTarget as HTMLElement ?? null)}
      />
      <Menu
        anchorEl={moreMenuAnchor}
        open={Boolean(moreMenuAnchor)}
        onClose={() => setMoreMenuAnchor(null)}
      >
        <MenuItem onClick={() => { setMoreMenuAnchor(null); handleAddRecipe(); }}>
          Nova Receita
        </MenuItem>
        <MenuItem onClick={() => { setMoreMenuAnchor(null); navigate('/recipes/calculator'); }}>
          Calculadora de Porções
        </MenuItem>
      </Menu>
      <Box sx={containerStyles}>
        {/* Prompt de upgrade se próximo do limite */}
        {recipesRemaining > 0 && recipesRemaining <= 2 && (
          <Box sx={{ mb: 3 }}>
            <UpgradePrompt
              feature="receitas"
              currentPlan={plan}
              onUpgrade={handleUpgrade}
            />
          </Box>
        )}

        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={handleAddRecipe}
          disabled={!canAddRecipe}
          sx={{
            width: '100%',
            mb: 2,
            backgroundColor: theme.colors.secondary,
            color: 'white',
            py: 1.5,
            borderRadius: theme.radii.medium,
            boxShadow: theme.shadows.small,
            '&:hover': {
              backgroundColor: theme.colors.secondary,
              filter: 'brightness(0.9)',
            },
            '&.Mui-disabled': {
              backgroundColor: theme.colors.secondary,
              color: 'white',
              opacity: 0.5,
            }
          }}
        >
          ADICIONAR RECEITA
        </Button>

        <Button
          variant="outlined"
          startIcon={<MenuBookIcon />}
          onClick={() => navigate('/recipes/explore')}
          sx={{
            width: '100%',
            mb: 2,
            color: theme.colors.primary,
            borderColor: theme.colors.primary,
            py: 1.5,
            borderRadius: theme.radii.medium,
            textTransform: 'uppercase',
            fontWeight: 'bold',
          }}
        >
          Explorar receitas públicas
        </Button>

        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 2 }}>
          <FormControlLabel
            sx={{ m: 0 }}
            control={
              <Switch
                checked={bookPublic}
                disabled={bookBusy}
                onChange={(e) => handleToggleBookPublic(e.target.checked)}
              />
            }
            label={
              <Box>
                <Typography variant="body2" sx={{ color: theme.colors.text.primary, fontWeight: 600 }}>
                  Tornar o livro público
                </Typography>
                <Typography variant="caption" sx={{ color: theme.colors.text.secondary }}>
                  Publica todas as suas receitas de uma vez
                </Typography>
              </Box>
            }
          />
          {bookBusy && <CircularProgress size={18} />}
        </Box>

        <Snackbar
          open={!!bookMessage || !!bookError}
          autoHideDuration={4000}
          onClose={() => { setBookMessage(null); setBookError(null); }}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert
            severity={bookError ? 'error' : 'success'}
            onClose={() => { setBookMessage(null); setBookError(null); }}
            sx={{ width: '100%' }}
          >
            {bookError || bookMessage}
          </Alert>
        </Snackbar>

        <Button
          variant="outlined"
          startIcon={<RestaurantIcon />} // Or CalculateIcon if imported, but Restaurant is already there
          onClick={() => navigate('/recipes/calculator')}
          sx={{
            width: '100%',
            mb: 3,
            borderColor: theme.colors.primary,
            color: theme.colors.primary,
            py: 1.5,
            borderRadius: theme.radii.medium,
            '&:hover': {
              borderColor: theme.colors.primary,
              backgroundColor: `${theme.colors.primary}10`,
            }
          }}
        >
          CALCULADORA DE PORÇÕES
        </Button>

        {/* Search Bar */}
        <TextField
          fullWidth
          placeholder="Buscar receitas..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: theme.colors.text.secondary }} />
              </InputAdornment>
            ),
          }}
          sx={searchFieldStyles}
        />

        {/* Category Filter */}
        <TextField
          select
          label="Filtrar por categoria"
          value={selectedCategory}
          onChange={(e) => setSelectedCategory(e.target.value)}
          fullWidth
          sx={{
            mb: { xs: 2, sm: 3 },
            '& .MuiOutlinedInput-root': {
              borderRadius: theme.radii.medium,
              backgroundColor: theme.colors.card,
              fontSize: { xs: '0.95rem', sm: '1rem' }
            }
          }}
          SelectProps={{
            native: true,
          }}
          slotProps={{
            inputLabel: {
              shrink: true,
              sx: { fontSize: { xs: '0.9rem', sm: '1rem' } }
            }
          }}
        >
          <option value="">Todas as categorias</option>
          {categories.map((category) => (
            <option key={category} value={category}>
              {category}
            </option>
          ))}
        </TextField>

        {/* Stats + View Toggle */}
        {recipes.length > 0 && (
          <Box sx={{ mb: { xs: 2, sm: 3 }, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Typography sx={{ color: theme.colors.text.secondary, fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                {displayRecipes.length} {displayRecipes.length === 1 ? 'receita' : 'receitas'}
              </Typography>
              {recipesRemaining !== -1 && (
                <Typography
                  sx={{
                    color: theme.colors.text.secondary,
                    fontSize: { xs: '0.8rem', sm: '0.875rem' },
                  }}
                >
                  ({recipesRemaining} {recipesRemaining === 1 ? 'restante' : 'restantes'})
                </Typography>
              )}
            </Box>
            <ToggleButtonGroup
              value={viewMode}
              exclusive
              onChange={(_, value) => value && setViewMode(value)}
              size="small"
              sx={{
                '& .MuiToggleButton-root': {
                  border: `1px solid ${theme.colors.border}`,
                  color: theme.colors.text.secondary,
                  padding: '4px 8px',
                  '&.Mui-selected': {
                    backgroundColor: `${theme.colors.primary}20`,
                    color: theme.colors.primary,
                    '&:hover': {
                      backgroundColor: `${theme.colors.primary}30`,
                    },
                  },
                },
              }}
            >
              <ToggleButton value="list" aria-label="list view">
                <ViewListIcon fontSize="small" />
              </ToggleButton>
              <ToggleButton value="grid" aria-label="grid view">
                <ViewModuleIcon fontSize="small" />
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
        )}

        {recipeLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: { xs: 6, sm: 8 } }}>
            <CircularProgress sx={{ color: theme.colors.primary }} />
          </Box>
        ) : recipes.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: { xs: 6, sm: 8 }, px: 2 }}>
            <RestaurantIcon
              sx={{
                fontSize: { xs: '3rem', sm: '4rem' },
                color: theme.colors.text.secondary,
                opacity: 0.3,
                mb: 2,
              }}
            />
            <Typography
              variant="h6"
              sx={{
                color: theme.colors.text.primary,
                mb: 1,
                fontSize: { xs: '1rem', sm: '1.25rem' }
              }}
            >
              Nenhuma receita encontrada
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: theme.colors.text.secondary,
                mb: 3,
                fontSize: { xs: '0.875rem', sm: '0.875rem' }
              }}
            >
              Clique no botão + para adicionar sua primeira receita
            </Typography>
          </Box>
        ) : displayRecipes.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: { xs: 6, sm: 8 }, px: 2 }}>
            <Typography sx={{ color: theme.colors.text.secondary, mb: 2, fontSize: { xs: '0.875rem', sm: '1rem' } }}>
              Nenhuma receita encontrada para "{searchTerm}"
            </Typography>
          </Box>
        ) : viewMode === 'grid' ? (
          /* Grid View */
          <Grid container spacing={{ xs: 1.5, sm: 2 }}>
            {displayRecipes.map((recipe) => (
              <Grid size={{ xs: 6, sm: 4, md: 3 }} key={recipe.id}>
                <Card
                  onClick={() => handleViewClick(recipe)}
                  sx={{
                    borderRadius: theme.radii.medium,
                    boxShadow: 'none',
                    backgroundColor: theme.colors.card,
                    border: `1px solid ${theme.colors.border}`,
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    position: 'relative',
                    overflow: 'hidden',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: theme.shadows.medium,
                      borderColor: theme.colors.primary,
                    }
                  }}
                >
                  {/* Action buttons */}
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 8,
                      right: 8,
                      display: 'flex',
                      gap: 0.5,
                      backgroundColor: `${theme.colors.background}E0`,
                      borderRadius: theme.radii.small,
                      padding: '2px',
                      zIndex: 2,
                    }}
                  >
                    <IconButton
                      size="small"
                      onClick={(e: React.MouseEvent) => handleEditClick(e, recipe)}
                      sx={{
                        color: theme.colors.primary,
                        padding: '4px',
                        '&:hover': { backgroundColor: `${theme.colors.primary}15` },
                      }}
                    >
                      <EditIcon sx={{ fontSize: '1rem' }} />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={(e: React.MouseEvent) => handleDeleteClick(e, recipe.id)}
                      sx={{
                        color: '#d32f2f',
                        padding: '4px',
                        '&:hover': { backgroundColor: 'rgba(211, 47, 47, 0.1)' },
                      }}
                    >
                      <DeleteIcon sx={{ fontSize: '1rem' }} />
                    </IconButton>
                  </Box>

                  {/* Recipe photo */}
                  {recipe.imageUrl ? (
                    <CardMedia
                      component="img"
                      image={recipe.imageUrl}
                      alt={recipe.title}
                      sx={{
                        aspectRatio: '1 / 1',
                        objectFit: 'cover',
                      }}
                    />
                  ) : (
                    <Box
                      sx={{
                        aspectRatio: '1 / 1',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: 'linear-gradient(135deg, #E1C4B5 0%, #D4A574 100%)',
                      }}
                    >
                      <RestaurantIcon sx={{ fontSize: '3rem', opacity: 0.4, color: theme.colors.text.secondary }} />
                    </Box>
                  )}

                  {/* Recipe title overlay at bottom */}
                  <Box
                    sx={{
                      p: 1,
                      '&:last-child': { pb: 1 },
                      backgroundColor: theme.colors.card,
                    }}
                  >
                    <Typography
                      sx={{
                        fontWeight: 'bold',
                        color: theme.colors.text.primary,
                        fontSize: '0.8rem',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        textAlign: 'center',
                      }}
                    >
                      {recipe.title}
                    </Typography>
                    <Typography
                      sx={{
                        color: theme.colors.text.secondary,
                        fontSize: '0.65rem',
                        textAlign: 'center',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {recipe.servings} porções • {recipe.prepTime} min
                    </Typography>
                  </Box>
                </Card>
              </Grid>
            ))}
          </Grid>
        ) : (
          /* List View */
          <Grid container spacing={{ xs: 1.5, sm: 2 }}>
            {displayRecipes.map((recipe) => (
              <Grid size={{ xs: 12, md: 6 }} key={recipe.id}>
                <Card
                  onClick={() => handleViewClick(recipe)}
                  sx={recipeCardStyles}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flex: 1, px: { xs: 1.5, sm: 2 } }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1.5, sm: 2 }, minWidth: 0, flex: 1 }}>
                      <Box
                        sx={{
                          width: { xs: 45, sm: 50 },
                          height: { xs: 45, sm: 50 },
                          flexShrink: 0,
                          borderRadius: theme.radii.small,
                          backgroundImage: recipe.imageUrl
                            ? `url(${recipe.imageUrl})`
                            : 'linear-gradient(135deg, #E1C4B5 0%, #D4A574 100%)',
                          backgroundSize: 'cover',
                          backgroundPosition: 'center',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        {!recipe.imageUrl && (
                          <RestaurantIcon sx={{ fontSize: { xs: '1.3rem', sm: '1.5rem' }, opacity: 0.5, color: theme.colors.text.secondary }} />
                        )}
                      </Box>
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography
                          sx={{
                            fontWeight: 'bold',
                            color: theme.colors.text.primary,
                            fontSize: { xs: '0.9rem', sm: '1rem' },
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {recipe.title}
                        </Typography>
                        <Typography sx={{
                          color: theme.colors.text.secondary,
                          fontSize: { xs: '0.75rem', sm: '0.875rem' },
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}>
                          {recipe.servings} porções • {recipe.prepTime} min
                          {recipe.category && ` • ${recipe.category}`}
                        </Typography>
                        {recipe.sharedBy && (
                          <Typography sx={{
                            color: theme.colors.primary,
                            fontSize: { xs: '0.7rem', sm: '0.75rem' },
                            fontStyle: 'italic',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}>
                            Enviada por {recipe.sharedBy}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                    <Box sx={{ display: 'flex', gap: { xs: 0.25, sm: 0.5 }, flexShrink: 0 }}>
                      <IconButton
                        size="small"
                        onClick={(e: React.MouseEvent) => handleEditClick(e, recipe)}
                        sx={{
                          color: theme.colors.primary,
                          p: { xs: 0.5, sm: 1 },
                          '&:hover': {
                            backgroundColor: `${theme.colors.primary}15`,
                          }
                        }}
                      >
                        <EditIcon sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }} />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={(e: React.MouseEvent) => handleDeleteClick(e, recipe.id)}
                        sx={{
                          color: '#d32f2f',
                          p: { xs: 0.5, sm: 1 },
                          '&:hover': {
                            backgroundColor: 'rgba(211, 47, 47, 0.1)',
                          }
                        }}
                      >
                        <DeleteIcon sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }} />
                      </IconButton>
                    </Box>
                  </Box>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </Box>

      {/* Floating Action Button */}


      {/* Dialog de CRUD */}
      <RecipesDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onSave={handleSaveRecipe}
        onUpdate={handleUpdateRecipe}
        onDelete={handleDeleteRecipe}
        recipe={selectedRecipe}
        mode={dialogMode}
      />

      {/* Dialog de limite atingido */}
      <LimitReachedDialog
        open={limitDialog.open}
        feature="receitas"
        onClose={() => setLimitDialog({ open: false, message: '' })}
        onUpgrade={handleUpgrade}
        currentPlan={plan}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => !deleting && setDeleteDialogOpen(false)}
        PaperProps={{
          sx: {
            borderRadius: theme.radii.large,
            backgroundColor: theme.colors.background,
          },
        }}
      >
        <DialogTitle sx={{ color: theme.colors.text.primary }}>
          Excluir receita?
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: theme.colors.text.secondary }}>
            Tem certeza que deseja excluir esta receita? Esta ação não pode ser desfeita.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => setDeleteDialogOpen(false)}
            disabled={deleting}
            sx={{
              color: theme.colors.text.primary,
              borderRadius: theme.radii.medium,
            }}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            disabled={deleting}
            sx={{
              backgroundColor: '#d32f2f',
              color: 'white',
              borderRadius: theme.radii.medium,
              '&:hover': {
                backgroundColor: '#d32f2f',
                opacity: 0.9,
              },
              '&:disabled': {
                backgroundColor: '#d32f2f',
                opacity: 0.5,
              }
            }}
          >
            {deleting ? <CircularProgress size={20} sx={{ color: 'white' }} /> : 'Excluir'}
          </Button>
        </DialogActions>
      </Dialog>
      <BottomNavigation />
    </Box>
  );
};

